import { useState, useCallback, useEffect, useRef } from "react"
import { message } from "antd"
import { UseInterviewMediaProps } from "../components/types"



export const useInterviewMedia = ({
    stream,
    setStream,
    videoRef,
    isCameraOn,
    setIsCameraOn,
    isMicOn,
    setIsMicOn,
    connected,
    isMyraSpeaking,
    isBlockedFromSpeakingRef,
    recognitionRef,
    setIsListening,
    micStreamRef,
    audioContextRef,
    destinationRef,
    micSourceNodeRef,
    micGainNodeRef,
}: UseInterviewMediaProps) => {
    const [isCameraStarting, setIsCameraStarting] = useState(false)
    const [isMicStarting, setIsMicStarting] = useState(false)
    const audioTrackRef = useRef<MediaStreamTrack | null>(null)

    // Ensure camera state
    useEffect(() => {
        let cancelled = false

        const ensureCameraState = async () => {
            const hasVideoTracks = Boolean(stream?.getVideoTracks()?.length)

            if (hasVideoTracks) {
                if (!isCameraOn) {
                    setIsCameraOn(true)
                }
                if (videoRef.current && videoRef.current.srcObject !== stream) {
                    videoRef.current.srcObject = stream
                    videoRef.current.play().catch(() => { })
                }
                return
            }

            if (!isCameraOn) {
                if (videoRef.current && videoRef.current.srcObject) {
                    videoRef.current.srcObject = null
                }
                return
            }

            setIsCameraStarting(true)
            try {
                let newVideoStream: MediaStream

                try {
                    newVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                            facingMode: "user",
                        },
                        audio: false,
                    })
                } catch (primaryError) {
                    newVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: true,
                        audio: false,
                    })
                }

                if (cancelled) {
                    newVideoStream.getTracks().forEach((track) => track.stop())
                    return
                }

                const videoTrack = newVideoStream.getVideoTracks()[0]
                const finalStream = stream ?? new MediaStream()
                finalStream.addTrack(videoTrack)
                setStream(finalStream)
                setIsCameraOn(true)

                if (videoRef.current) {
                    videoRef.current.srcObject = finalStream
                    videoRef.current.play().catch(() => { })
                }
            } catch (error) {
                console.error("Error auto-starting camera:", error)
                setIsCameraOn(false)
            } finally {
                setIsCameraStarting(false)
            }
        }

        const timer = window.setTimeout(ensureCameraState, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [isCameraOn, setIsCameraOn, setStream, stream, videoRef])

    // Monitor microphone track ended event
    useEffect(() => {
        if (!stream) return

        const audioTracks = stream.getAudioTracks()
        if (audioTracks.length === 0) return

        const audioTrack = audioTracks[0]

        const handleTrackEnded = () => {
            setIsMicOn(false)
        }

        audioTrack.addEventListener('ended', handleTrackEnded)

        return () => {
            audioTrack.removeEventListener('ended', handleTrackEnded)
        }
    }, [stream, setIsMicOn])

    const toggleCamera = useCallback(async () => {
        if (isCameraOn) {
            if (stream) {
                const videoTracks = stream.getVideoTracks()
                if (videoTracks.length > 0) {
                    videoTracks[0].stop()
                    stream.removeTrack(videoTracks[0])
                }
                if (stream.getVideoTracks().length === 0 && stream.getAudioTracks().length === 0) {
                    setStream(null)
                }
            }
            setIsCameraOn(false)
            if (videoRef.current) {
                videoRef.current.srcObject = null
            }
        } else {
            setIsCameraStarting(true)
            try {
                let newVideoStream: MediaStream

                try {
                    newVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                            facingMode: "user",
                        },
                        audio: false,
                    })
                } catch (primaryError) {
                    newVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: true,
                        audio: false,
                    })
                }

                const videoTrack = newVideoStream.getVideoTracks()[0]
                let finalStream: MediaStream

                if (stream) {
                    stream.addTrack(videoTrack)
                    finalStream = stream
                } else {
                    finalStream = new MediaStream([videoTrack])
                    setStream(finalStream)
                }

                setTimeout(() => {
                    if (videoRef.current && finalStream) {
                        videoRef.current.srcObject = finalStream
                        videoRef.current.play().catch(() => { })
                    }
                }, 100)

                setIsCameraOn(true)
            } catch (error) {
                let errorMessage = "Unable to access camera"

                if (error instanceof Error) {
                    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
                        errorMessage = "Camera permission denied. Please allow camera access and try again."
                    } else if (error.name === "NotFoundError") {
                        errorMessage = "No camera found on this device."
                    } else if (error.name === "NotReadableError") {
                        errorMessage = "Camera is being used by another application."
                    }
                }

                message.error(errorMessage)
                setIsCameraOn(false)
            } finally {
                setIsCameraStarting(false)
            }
        }
    }, [isCameraOn, stream, setStream, setIsCameraOn, videoRef])

    const toggleMicrophone = useCallback(async (onWarning: () => void) => {
        if (isMicOn) {
            // Cannot turn off mic - show warning
            onWarning()
            return
        } else {
            setIsMicStarting(true)
            try {
                const audioConstraints = {
                    audio: {
                        echoCancellation: { ideal: true, exact: true },
                        noiseSuppression: { ideal: true, exact: true },
                        autoGainControl: { ideal: true, exact: true },
                        sampleRate: { ideal: 48000 },
                        channelCount: { ideal: 1 },
                        volume: { ideal: 1.0 },
                        latency: { ideal: 0.01 },
                    },
                    video: false,
                }

                let newAudioStream: MediaStream
                try {
                    newAudioStream = await navigator.mediaDevices.getUserMedia(audioConstraints)
                } catch (enhancedError) {
                    newAudioStream = await navigator.mediaDevices.getUserMedia({
                        audio: {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true,
                        },
                        video: false,
                    })
                }

                const audioTrack = newAudioStream.getAudioTracks()[0]
                audioTrackRef.current = audioTrack

                // Monitor if microphone track ends (permission revoked)
                audioTrack.addEventListener('ended', () => {
                    setIsMicOn(false)
                    audioTrackRef.current = null
                })

                // Update the mic stream ref used in recording
                micStreamRef.current = newAudioStream

                // Reconnect the new mic stream to the audio context for recording with gain control
                if (audioContextRef.current && destinationRef.current) {
                    try {
                        const newMicSource = audioContextRef.current.createMediaStreamSource(newAudioStream)
                        const newMicGain = audioContextRef.current.createGain()
                        newMicGain.gain.value = 1.0

                        newMicSource.connect(newMicGain)
                        newMicGain.connect(destinationRef.current)

                        micSourceNodeRef.current = newMicSource
                        micGainNodeRef.current = newMicGain
                    } catch (error) {
                        console.error("❌ Error reconnecting mic to audio context:", error)
                    }
                }

                if (stream) {
                    stream.addTrack(audioTrack)
                }

                setIsMicOn(true)

                // Restart speech recognition after microphone is ready
                if (connected && !isMyraSpeaking && !isBlockedFromSpeakingRef.current && recognitionRef.current) {
                    setTimeout(() => {
                        try {
                            recognitionRef.current.start()
                            setIsListening(true)
                        } catch (error) {
                            console.error("Error restarting recognition:", error)
                        }
                    }, 500)
                }
            } catch (error) {
                message.error("Unable to access microphone")
                audioTrackRef.current = null
                micStreamRef.current = null
            } finally {
                setIsMicStarting(false)
            }
        }
    }, [
        isMicOn,
        setIsMicOn,
        stream,
        connected,
        isMyraSpeaking,
        isBlockedFromSpeakingRef,
        recognitionRef,
        setIsListening,
        micStreamRef,
        audioContextRef,
        destinationRef,
        micSourceNodeRef,
        micGainNodeRef,
    ])

    return {
        isCameraStarting,
        isMicStarting,
        audioTrackRef,
        toggleCamera,
        toggleMicrophone,
    }
}

